import React, { useState, useEffect } from 'react'
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowLeft, Send, Key } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

const Login = () => {
  const { login } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const isBlocked = error && error.toLowerCase().includes('bloqueada')
  const [attemptInfo, setAttemptInfo] = useState(null)
  
  // Estados para recuperação de senha
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryData, setRecoveryData] = useState({ contato: '' })
  const [recoveryLoading, setRecoveryLoading] = useState(false)
  const [recoveryMessage, setRecoveryMessage] = useState('')
  
  // Estados para redefinição de senha
  const [showResetForm, setShowResetForm] = useState(false)
  const [resetData, setResetData] = useState({
    token: '',
    nova_senha: '',
    confirmar_senha: ''
  })
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMessage, setResetMessage] = useState('')
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('login_block_until')
      if (stored) {
        const until = parseInt(stored, 10)
        if (!isNaN(until) && Date.now() < until) {
          const mins = Math.ceil((until - Date.now()) / (60 * 1000))
          setError(`Conta bloqueada por muitas tentativas. Aguarde ${mins} minutos.`)
          setAttemptInfo({ usadas: 5, max: 5, restantes: 0, bloqueado: true, minutos: mins })
        } else {
          localStorage.removeItem('login_block_until')
        }
      }
    } catch {}
  }, [])
  
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('') // Limpar erro ao digitar
  }
  
  const handleRecoveryChange = (e) => {
    setRecoveryData({ contato: e.target.value })
    setRecoveryMessage('')
  }
  
  const handleResetChange = (e) => {
    const { name, value } = e.target
    setResetData(prev => ({
      ...prev,
      [name]: value
    }))
    setResetMessage('')
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    try {
      const result = await login(formData)
      
      if (result.success) {
        // Login bem-sucedido - o AuthContext já atualizou o estado
        try { localStorage.removeItem('login_block_until') } catch {}
      } else {
        setError(result.error || 'Erro no login')
        if (typeof result.tentativasUsadas === 'number') {
          setAttemptInfo({
            usadas: result.tentativasUsadas,
            max: result.tentativasMax,
            restantes: result.tentativasRestantes,
            bloqueado: !!result.bloqueado,
            minutos: result.bloqueioRestanteMinutos || null
          })
          if (result.bloqueado && result.bloqueioRestanteMinutos) {
            try {
              const until = Date.now() + (result.bloqueioRestanteMinutos * 60 * 1000)
              localStorage.setItem('login_block_until', String(until))
            } catch {}
          }
        } else {
          setAttemptInfo(null)
        }
      }
    } catch (err) {
      console.error('Erro no login:', err)
      setError('Erro de conexão. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }
  
  const handleRecoverySubmit = async (e) => {
    e.preventDefault()
    setRecoveryLoading(true)
    setRecoveryMessage('')
    
    try {
      const response = await api.post('/auth/recuperar-senha.php', recoveryData)
      const data = response
      
      if (data.ok) {
        setRecoveryMessage(`✅ ${data.message}`)
        // Automaticamente abrir o formulário de redefinição
        setTimeout(() => {
          setShowResetForm(true)
        }, 1000)
      } else {
        setRecoveryMessage(`❌ ${data.message}`)
      }
    } catch (err) {
      console.error('Erro na recuperação:', err)
      setRecoveryMessage('❌ Erro de conexão. Tente novamente.')
    } finally {
      setRecoveryLoading(false)
    }
  }
  
  const handleResetSubmit = async (e) => {
    e.preventDefault()
    setResetLoading(true)
    setResetMessage('')
    
    // Validações
    if (resetData.nova_senha.length < 6) {
      setResetMessage('❌ A senha deve ter pelo menos 6 caracteres')
      setResetLoading(false)
      return
    }
    
    if (resetData.nova_senha !== resetData.confirmar_senha) {
      setResetMessage('❌ As senhas não coincidem')
      setResetLoading(false)
      return
    }
    
    try {
      const response = await api.post('/auth/redefinir-senha.php', {
        token: resetData.token,
        nova_senha: resetData.nova_senha,
        confirmar_senha: resetData.confirmar_senha
      })
      const data = response
      
      if (data.ok) {
        setResetMessage('✅ Senha redefinida com sucesso!')
        // Limpar formulário e voltar para login
        setTimeout(() => {
          setShowResetForm(false)
          setShowRecovery(false)
          setResetData({ token: '', nova_senha: '', confirmar_senha: '' })
          setResetMessage('')
        }, 2000)
      } else {
        setResetMessage(`❌ ${data.message}`)
      }
    } catch (err) {
      console.error('Erro na redefinição:', err)
      setResetMessage('❌ Erro de conexão. Tente novamente.')
    } finally {
      setResetLoading(false)
    }
  }
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }
  
  const toggleRecovery = () => {
    setShowRecovery(!showRecovery)
    setRecoveryMessage('')
    setRecoveryData({ contato: '' })
    setShowResetForm(false)
    setResetData({ token: '', nova_senha: '', confirmar_senha: '' })
    setResetMessage('')
  }
  
  const toggleResetForm = () => {
    setShowResetForm(!showResetForm)
    setResetData({ token: '', nova_senha: '', confirmar_senha: '' })
    setResetMessage('')
  }
  
  // Se estiver mostrando o formulário de redefinição
  if (showResetForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <Key className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Redefinir Senha
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Cole o token recebido e digite sua nova senha
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleResetSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="token" className="sr-only">
                  Token de Recuperação
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="token"
                    name="token"
                    type="text"
                    required
                    className="input pl-10 w-full"
                    placeholder="Cole o token recebido"
                    value={resetData.token}
                    onChange={handleResetChange}
                    maxLength={32}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="nova_senha" className="sr-only">
                  Nova Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="nova_senha"
                    name="nova_senha"
                    type={showResetPassword ? 'text' : 'password'}
                    required
                    className="input pl-10 pr-10 w-full"
                    placeholder="Nova senha"
                    value={resetData.nova_senha}
                    onChange={handleResetChange}
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                  >
                    {showResetPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <div>
                <label htmlFor="confirmar_senha" className="sr-only">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmar_senha"
                    name="confirmar_senha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="input pl-10 pr-10 w-full"
                    placeholder="Confirme a nova senha"
                    value={resetData.confirmar_senha}
                    onChange={handleResetChange}
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {resetMessage && (
              <div className={`text-sm p-3 rounded-md ${
                resetMessage.includes('✅') 
                  ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {resetMessage}
              </div>
            )}
            
            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={resetLoading}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Redefinir Senha'
                )}
              </button>
              
              <button
                type="button"
                onClick={toggleResetForm}
                className="btn-outline w-full"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Voltar à Recuperação
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
  
  // Se estiver mostrando o formulário de recuperação
  if (showRecovery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
              <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Recuperar Senha
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Digite seu email ou telefone para receber o token de recuperação
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleRecoverySubmit}>
            <div>
              <label htmlFor="contato" className="sr-only">
                Email ou Telefone
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="contato"
                  name="contato"
                  type="text"
                  required
                  className="input pl-10 w-full"
                  placeholder="Email ou telefone"
                  value={recoveryData.contato}
                  onChange={handleRecoveryChange}
                />
              </div>
            </div>
            
            {recoveryMessage && (
              <div className={`text-sm p-3 rounded-md ${
                recoveryMessage.includes('✅') 
                  ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {recoveryMessage}
              </div>
            )}
            
            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={recoveryLoading}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {recoveryLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Enviar Token
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={toggleRecovery}
                className="btn-outline w-full"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Voltar ao Login
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
  
  // Formulário de login principal
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
            <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Login Gabinett
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Acesse sua conta para continuar
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input pl-10 w-full"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="senha" className="sr-only">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="senha"
                  name="senha"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="input pl-10 pr-10 w-full"
                  placeholder="Senha"
                  value={formData.senha}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {isBlocked ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-md text-sm">
              🔒 {error}
            </div>
          ) : (
            error && (
              <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )
          )}
          {attemptInfo && !attemptInfo.bloqueado && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Tentativa {attemptInfo.usadas}/{attemptInfo.max}
            </div>
          )}
          {attemptInfo && !attemptInfo.bloqueado && attemptInfo.usadas >= 3 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-md text-xs">
              Atenção: continuar pode bloquear o acesso por 15 minutos.
            </div>
          )}
          
          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={loading || isBlocked}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Entrar'
              )}
            </button>
            
            <button
              type="button"
              onClick={toggleRecovery}
              className="btn-outline w-full"
            >
              Esqueci minha senha
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
